import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { NS } from '@/common/namespaces';
import SvgEditService from '@/services/svg-edit-service';
import ZenDataService from '@/services/zen-data-service';
import PanelsService from '@/services/panels-service';
import { ToolDialog, ToolDialogOpenDirection } from '@/components/tool-dialog';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import { SvgAnnotations } from '../svg-annotations';
import styles from './pdTagEditor.module.scss';
import { isPDComponentElement } from '@/common/pdUtils';
import { PDTagEditor_Tagging, PDTagEditor_ComponentRepeatOptions, PDTagEditor_ComponentRules, PDTagEditor_Help } from './tageditor';
import { ErrorBoundary } from '@/components/error-boundary';

interface State {
    selectedTabIndex: number,
}

export class TagEditor extends React.Component<{}, State> {
    private getDefaultState = () => {
        const state: State = {
            selectedTabIndex: 0,
        }

        return state;
    }

    state = this.getDefaultState();

    private svgEditService = SvgEditService.Get(this, () => this.selectedElementChanged());
    private zenDataService = ZenDataService.Get(this);
    private panelService = PanelsService.Get(this);

    private dialog: React.RefObject<ToolDialog> = React.createRef();

    public componentWillUnmount() {
        this.svgEditService.UnregisterComponent(this);
        this.zenDataService.UnregisterComponent(this);
        this.panelService.UnregisterComponent(this);
    }

    private selectedElementChanged = () => {
        this.setState(prevState => {
            let selectedTabIndex = prevState.selectedTabIndex;

            if (this.svgEditService.selectedElements.length !== 1) {
                if (selectedTabIndex !== 0 && selectedTabIndex !== 3) {
                    selectedTabIndex = 0;
                }
            } else {
                const el = this.svgEditService.selectedElements[0];

                if (!isPDComponentElement(el)) {
                    if (selectedTabIndex !== 0 && selectedTabIndex !== 3) {
                        selectedTabIndex = 0;
                    }
                } else if (!el.hasAttribute("zaa:component-tag") && selectedTabIndex === 1) {
                    selectedTabIndex = 0;
                }
            }

            return { selectedTabIndex };
        });
    }

    public openToolDialog = (relativeTo: HTMLElement) => {
        this.dialog.current.open(relativeTo, ToolDialogOpenDirection.Right);
    }

    public render() {
        const el = this.svgEditService.selectedElements[0];
        const isComponentEl = isPDComponentElement(el);
        const hasComponentTag = el?.hasAttribute("zaa:component-tag") ?? false;

        const componentTabRepeatOptionsStyle = !isComponentEl || !hasComponentTag ? { display: "none" } : {};
        const componentTabRulesStyle = !isComponentEl ? { display: "none" } : {};

        return (
            <ToolDialog ref={this.dialog} id="pd-tageditor" title="Tag Editor" classes={styles.pdTagEditor}>
                <Tabs selectedIndex={this.state.selectedTabIndex} onSelect={idx => this.setState({ selectedTabIndex: idx })}>
                    <TabList>
                        <Tab>Tagging</Tab>
                        <Tab disabled={!isComponentEl || !hasComponentTag} style={componentTabRepeatOptionsStyle}>Repeat Options</Tab>
                        <Tab disabled={!isComponentEl} style={componentTabRulesStyle}>Rules</Tab>
                        <Tab>Help</Tab>
                    </TabList>

                    <TabPanel>
                        <ErrorBoundary>
                            <PDTagEditor_Tagging />
                        </ErrorBoundary>
                    </TabPanel>
                    <TabPanel>
                        <ErrorBoundary>
                            <PDTagEditor_ComponentRepeatOptions />
                        </ErrorBoundary>
                    </TabPanel>
                    <TabPanel>
                        <ErrorBoundary>
                            <PDTagEditor_ComponentRules />
                        </ErrorBoundary>
                    </TabPanel>
                    <TabPanel>
                        <ErrorBoundary>
                            <PDTagEditor_Help />
                        </ErrorBoundary>
                    </TabPanel>
                </Tabs>
            </ToolDialog>
        );
    }
}

SvgAnnotations.RegisterAnnotationCallback("TAGEDITOR", ({ element, textAnnotations, panelService }) => {
    const tagComponent = element.getAttribute("zaa:component-tag");
    const tagContent = element.getAttribute("zaa:content-tag");
    const tagFill = element.getAttribute("zaa:fill-tag");
    const tagStroke = element.getAttribute("zaa:stroke-tag");

    const formatTag = (tagStr: string, onlyShowMappingName: boolean = false): JSX.Element => {
        tagStr = tagStr.trim().replace("\n", " ").replace("&#10;", " ");

        if (tagStr.length > 76) {
            return <span>Raw Tag: &lt;&lt; Tag too long - View in Tag Editor &gt;&gt;</span>;
        }

        const match = tagStr.match(/\${{(.+)}}/);
        const matchVal = match?.[1];
        if (matchVal) {
            const tag = panelService.panelTagMappings.find(m => m.tag == matchVal);
            if (tag) {
                if (onlyShowMappingName) {
                    return <div>{tag.name}</div>;
                }

                return (
                    <>
                        <div>Raw Tag: {tagStr}</div>
                        <div>Config Value: {tag.name}</div>
                    </>
                );
            }
        }

        return <span>Raw Tag: {tagStr.replace("&#10;", " ")}</span>;
    }

    textAnnotations.push(
        <table key="element-tag-details">
            <tbody>
                {tagComponent && (
                    <tr>
                        <td>
                            Tagged Component
                        </td>
                        <td>
                            {formatTag('${{' + tagComponent + '}}')}
                        </td>
                    </tr>
                )}
                {tagContent && (
                    <tr>
                        <td>
                            Tagged Text
                        </td>
                        <td>
                            {formatTag(tagContent)}
                        </td>
                    </tr>
                )}
                {tagFill && (
                    <tr>
                        <td>
                            Tagged Fill
                        </td>
                        <td>
                            {formatTag(tagFill)}
                        </td>
                    </tr>
                )}
                {tagStroke && (
                    <tr>
                        <td>
                            Tagged Stroke
                        </td>
                        <td>
                            {formatTag(tagStroke)}
                        </td>
                    </tr>
                )}
            </tbody>
        </table>
    );
});
